import { useEffect, useState } from "react";
import { ArrowUpRight, BookmarkX } from "lucide-react";
import { AccountLayout } from "@/components/candidate/AccountLayout";
import { apiFetch, formatDate } from "@/components/candidate/shared";

export default function SavedJobs() {
    const [jobs, setJobs] = useState(null);

    const load = () =>
        apiFetch("/candidate/saved-jobs")
            .then((r) => (r.ok ? r.json() : []))
            .then(setJobs)
            .catch(() => setJobs([]));

    useEffect(() => {
        load();
    }, []);

    const remove = async (jobId) => {
        await apiFetch(`/candidate/saved-jobs/${jobId}`, { method: "DELETE" });
        load();
    };

    return (
        <AccountLayout title="Saved Jobs" subtitle="Roles you've bookmarked to revisit or apply to later.">
            {jobs === null ? (
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-forest border-t-transparent" />
            ) : jobs.length === 0 ? (
                <p data-testid="saved-empty" className="border-t border-ink/10 py-16 text-center text-sm text-ink/50">
                    Nothing saved yet — tap <span className="font-medium text-ink">Save</span> on any role at{" "}
                    <a href="/jobs" className="font-medium text-forest">Find Jobs</a>.
                </p>
            ) : (
                <div data-testid="saved-jobs-list">
                    {jobs.map((job) => (
                        <div
                            key={job.id}
                            data-testid={`saved-row-${job.slug}`}
                            className="grid grid-cols-[1fr_auto] items-center gap-x-6 gap-y-3 border-t border-ink/10 px-2 py-5"
                        >
                            <div className="min-w-0">
                                <a href={`/jobs/${job.slug}`} className="block truncate font-serif text-lg font-medium tracking-tight text-ink transition-colors hover:text-forest">
                                    {job.title}
                                </a>
                                <p className="text-xs text-ink/50">
                                    {job.company} · {job.city ? `${job.city}, ` : ""}{job.country} · Saved {formatDate(job.saved_at)}
                                    {job.status !== "published" && (
                                        <span className="ml-2 border border-ink/15 px-2 py-0.5 text-[9px] uppercase tracking-[0.16em] text-ink/40">
                                            No longer open
                                        </span>
                                    )}
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                {job.status === "published" && (
                                    <a
                                        href={`/jobs/${job.slug}#apply`}
                                        data-testid={`saved-apply-${job.slug}`}
                                        className="inline-flex items-center gap-2 bg-forest px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-ivory transition-colors hover:bg-forest-deep"
                                    >
                                        Apply
                                        <ArrowUpRight size={13} strokeWidth={1.5} />
                                    </a>
                                )}
                                <button
                                    type="button"
                                    data-testid={`saved-remove-${job.slug}`}
                                    onClick={() => remove(job.id)}
                                    aria-label={`Remove ${job.title}`}
                                    className="border border-ink/15 p-2.5 text-ink/45 transition-colors hover:border-ink/40 hover:text-ink"
                                >
                                    <BookmarkX size={15} strokeWidth={1.5} />
                                </button>
                            </div>
                        </div>
                    ))}
                    <div className="border-t border-ink/10" />
                </div>
            )}
        </AccountLayout>
    );
}
